import React from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';

interface Option {
  value: string | number;
  label: string;
}

interface ComboBoxProps {
  options: Option[] | string[];
  value: string | number | string[];
  onChange: (value: string | number | string[]) => void;
  className?: string;
  multiple?: boolean;
  label?: string;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  value,
  onChange,
  className,
  multiple = false,
  label
}) => {
  // Convert string array to Option array if needed
  const normalizedOptions: Option[] = options.map(option =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const handleChange = (newValue: string | number | string[]) => {
    // Ensure we always pass a string for single selection
    if (!multiple && Array.isArray(newValue)) {
      onChange(newValue[0] || '');
    } else if (
      !multiple &&
      (typeof newValue === 'string' || typeof newValue === 'number')
    ) {
      onChange(newValue);
    } else {
      onChange(newValue);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className='block text-sm font-medium text-slate-700 mb-2'>
          {label}
        </label>
      )}
      <Listbox value={value} onChange={handleChange} multiple={multiple}>
        <div className='relative w-full'>
          <Listbox.Button className='w-full rounded-full border border-gray-300 bg-white py-2 px-4 pr-12 text-left font-medium text-slate-900  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none'>
            <span className='block truncate'>
              {multiple
                ? Array.isArray(value) && value.length > 0
                  ? normalizedOptions
                      .filter(o => value.includes(String(o.value)))
                      .map(o => o.label)
                      .join(', ')
                  : 'Select...'
                : normalizedOptions.find(o => String(o.value) === String(value))
                    ?.label || normalizedOptions[0]?.label}
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center'>
              <ChevronDown className='h-4 w-4 text-slate-400' />
            </span>
          </Listbox.Button>
          <Listbox.Options className='absolute z-20 mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-base overflow-hidden'>
            {normalizedOptions.map((option, idx) => (
              <Listbox.Option
                key={option.value}
                value={option.value}
                className={({ active, selected }) =>
                  `cursor-pointer select-none relative py-2 px-4 ${
                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                  } ${selected ? 'font-semibold' : ''}` +
                  (idx === 0 ? ' rounded-t-lg' : '') +
                  (idx === normalizedOptions.length - 1 ? ' rounded-b-lg' : '')
                }
              >
                {multiple ? (
                  <span className='flex items-center'>
                    <input
                      type='checkbox'
                      checked={
                        Array.isArray(value) &&
                        value.includes(String(option.value))
                      }
                      readOnly
                      className='mr-2'
                    />
                    {option.label}
                  </span>
                ) : (
                  option.label
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

export default ComboBox;
